import { useEffect, useState } from "react";
import { Card, Table, Button } from "react-bootstrap";
import { jsPDF } from "jspdf";
import autoTable from 'jspdf-autotable';
import ReportFilter from "./ReportFilter";
import api from '../../config/api';
import { getDate, rupeeFormat, LocalFormatDate } from "../../config/functions";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, StoreState } from "../../redux/store";
import { ClientRegistrationType, fetchClientRegistrationAction } from "../../redux/actions/clientRegistration";
import { apiUrl } from "../../config";
import bgMaterial from "../../components/custom/assets/images/bg-material.png";
import SalesReturnReportFilter from "./SalesReturnReportFilter";


interface GstData {
    financialYear: string;
    invoice_number: string;
    ZERO_TAXABLE: string;
    FIVE_TAXABLE: string;
    TWELVE_TAXABLE: string;
    EIGHTEEN_TAXABLE: string;
    TWENTYEIGHT_TAXABLE: string;
    CGST: number;
    SGST: number;
    NET_GST: number;
    NET_AMT: number;
}

const SalesReturnGstRateWise = () => {
    const dispatch = useDispatch<AppDispatch>()

    const [fromDate, setFromDate] = useState<string>("");
    const [toDate, setToDate] = useState<string>("");
    const clients = useSelector<StoreState, ClientRegistrationType[]>(state => state.clientRegistration)

    const [triggerSearch, setTriggerSearch] = useState<boolean>(false);

    const [tableData, setTableData] = useState<GstData[]>([]);

    useEffect(() => {
        dispatch(fetchClientRegistrationAction())
    }, [])

    useEffect(() => {
        if (triggerSearch) {
            const response = api().get(
                `reports/salesReturnGSTWiseByInvoiceDate/?fromDate=${getDate(fromDate)}&toDate=${getDate(toDate)}`
            );

            response.then((res) => {
                if (res.status === 200 && res.data.data) {
                    console.log("API Response:", res.data);
                    setTableData(res.data.data);
                }
            }).catch(error => {
                console.error("Error fetching sales data:", error);
            });

            setTriggerSearch(false);
        }
    }, [triggerSearch, fromDate, toDate]);

    const [filters, setFilters] = useState<any>({});

    const handleSearch = (newFilters: any) => {
        setFilters(newFilters);
        setFromDate(newFilters.fromDate);
        setToDate(newFilters.toDate);

        setTriggerSearch(true);
    };
    const downloadCSV = () => {
        const header = [
            "SI.No", "Financial Year","Invoice No", "0%", "5% Taxable", "12% Taxable", "18% Taxable", "28% Taxable",
            "CGST", "SGST", "NETGST", "Net Amount"
        ].map(col => `="${col}"`);

        const rows = tableData.map((item, index) => [
            index + 1, // Ensures SI.No is treated as text
            item.financialYear,
            item.invoice_number,
            item.ZERO_TAXABLE,
            item.FIVE_TAXABLE,
            item.TWELVE_TAXABLE,
            item.EIGHTEEN_TAXABLE,
            item.TWENTYEIGHT_TAXABLE,
            item.CGST,
            item.SGST,
            item.NET_GST,
            item.NET_AMT
        ]);

        const csvContent = [
            header.join(","),
            ...rows.map(row => row.join(","))
        ].join("\n");

        const blob = new Blob([csvContent], { type: "text/csv" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = "sales_return_gst_rate_wise_report.csv";
        link.click();
    };


    const downloadPDF = () => {
        const logoUrl = clients[0]?.shopPhoto;

        const doc = new jsPDF("landscape");
        const marginX = 10;
        const boxWidth = doc.internal.pageSize.width - 2 * marginX;
        const boxHeight = 25;
        let startY = 15;

        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");

        doc.rect(marginX, startY - 5, boxWidth, boxHeight);

        if (logoUrl) {
            doc.addImage(logoUrl, "JPEG", marginX + 5, startY - 2, 25, 20);
        }

        const textX = marginX + 35;

        doc.text(clients[0]?.shopShortName || "", textX, startY);
        doc.setFont("helvetica", "normal");
        doc.text(
            `${clients[0]?.address1 || ""}, ${clients[0]?.address2 || ""}, ${clients[0]?.district || ""} `,
            textX,
            startY + 5
        );
        doc.text(
            `${clients[0]?.state || ""} - ${clients[0]?.pincode || ""}, ${clients[0]?.contactNo1 || ""}`,
            textX,
            startY + 10
        );
        doc.text(`GSTIN: ${clients[0]?.gstNo || ""}`, textX, startY + 15);

        doc.setFont("helvetica", "bold");
        doc.setFontSize(14);
        const heading = "Sales Return GST rate wise report";
        const headingWidth = doc.getTextWidth(heading);
        const pageWidth = doc.internal.pageSize.width;
        doc.text(heading, marginX + boxWidth - headingWidth - 10, startY + 8);

        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        const financialYearText = `Financial Year: ${tableData[0]?.financialYear || ""}`;
        const headingStartX = marginX + boxWidth - headingWidth - 10;
        doc.text(financialYearText, headingStartX, startY + 13);
        
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");

        const fromDate = "From : " + (LocalFormatDate(getDate(filters?.fromDate)) || "");
        const toDate = "To : " + (LocalFormatDate(getDate(filters?.toDate)) || "");

        const tableStartY = startY + boxHeight + 10;

        doc.text(fromDate, marginX, tableStartY - 8);
        doc.text(toDate, marginX + 50, tableStartY - 8);

        if (!tableData || tableData.length === 0) {
            doc.text("No data available", marginX, tableStartY);
            doc.save("sales_return_gst_rate_wise.pdf");
            return;
        }

        let tableBody = tableData.map((item, index) => [
            (index + 1).toString(),
            item.invoice_number || "",
            rupeeFormat(item.ZERO_TAXABLE) || "",
            rupeeFormat(item.FIVE_TAXABLE) || "",
            rupeeFormat(item.TWELVE_TAXABLE) || "",
            rupeeFormat(item.EIGHTEEN_TAXABLE) || "",
            rupeeFormat(item.TWENTYEIGHT_TAXABLE) || "",
            rupeeFormat(item.CGST) || "0",
            rupeeFormat(item.SGST) || "0",
            rupeeFormat(item.NET_GST) || "0",
            rupeeFormat(item.NET_AMT) || "0",
        ]);

        autoTable(doc, {
            startY: 47,
            head: [
                [
                    "SI.No", "Invoice No", "0%", "5% Taxable", "12% Taxable", "18% Taxable", "28% Taxable",
                    "CGST", "SGST", "NETGST", "Net Amount"
                ],
            ],
            body: tableBody,
            theme: "grid",
            styles: { fontSize: 8, textColor: 0, lineColor: 0, lineWidth: 0.1 },
            headStyles: {
                fillColor: [255, 255, 255],
                textColor: 0,
                fontStyle: "bold",
                lineColor: 0,
                lineWidth: 0.1,
            },
            margin: { top: 25, left: 10, right: 10 },
            columnStyles: {
                0: { halign: "center" },
                1: { halign: "center" },
                2: { halign: "right" },
                3: { halign: "right" },  
                4: { halign: "right" },  
                5: { halign: "right" }, 
                6: { halign: "right" },  
                7: { halign: "right" },  
                8: { halign: "right" }, 
                9: { halign: "right" }, 
                10: { halign: "right" },  
            },
            
        });

        doc.save("sales_return_gst_rate_wise.pdf");
    };


    return (
        <>
            <SalesReturnReportFilter onSearch={handleSearch} />
            {tableData.length > 0 ?
                <Card className="mt-2 p-2 shadow-lg border-0 rounded-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <h5 className="fw-bold">Sales Return GST rate wise details</h5>
                        <div>
                            <Button className="me-2 btn-sm" variant="success" onClick={downloadCSV}>
                                Download CSV
                            </Button>
                            <Button className="me-2 btn-sm" variant="success" onClick={downloadPDF}>
                                Download PDF
                            </Button>
                        </div>
                    </div>
                    <Table striped bordered hover>
                        <thead>
                            <tr>
                                <th>SI.No</th>
                                <th>Financial Year</th>
                                <th>Invoice No</th>
                                <th>0%</th>
                                <th>5% Taxable</th>
                                <th>12% Taxable</th>
                                <th>18% Taxable</th>
                                <th>28% Taxable</th>
                                <th>CGST</th>
                                <th>SGST</th>
                                <th>Net GST</th>
                                <th>Net Amount</th>
                            </tr>
                        </thead>

                        <tbody>
                            {tableData.map((item, index) => (
                                <tr key={index}>
                                    <td>{index + 1}</td>
                                    <td>{item.financialYear}</td>
                                    <td>{item.invoice_number}</td>
                                    <td>{rupeeFormat(item.ZERO_TAXABLE)}</td>
                                    <td>{rupeeFormat(item.FIVE_TAXABLE)}</td>
                                    <td>{rupeeFormat(item.TWELVE_TAXABLE)}</td>
                                    <td>{rupeeFormat(item.EIGHTEEN_TAXABLE)}</td>
                                    <td>{rupeeFormat(item.TWENTYEIGHT_TAXABLE)}</td>
                                    <td>{rupeeFormat(item.CGST)}</td>
                                    <td>{rupeeFormat(item.SGST)}</td>
                                    <td>{rupeeFormat(item.NET_GST)}</td>
                                    <td>{rupeeFormat(item.NET_AMT)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>

                </Card> : <h4 className="text-center fw-bold text-secondary">
                    No such data found !
                </h4>
            }
        </>
    );
};

export default SalesReturnGstRateWise;
